'use client';
import { PremiumBackground } from '../effects/PremiumBackground';

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown, Shield, CheckCircle2, Activity, Lock } from 'lucide-react';
import { LiquidLensCanvas } from '../effects/LiquidLensCanvas';
import { LiquidGlassCard } from '../effects/LiquidGlassCard';

// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface HeroSectionProps {
  id?: string;
}

export function HeroSection({ id = 'hero' }: HeroSectionProps = {}) {
    const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Структура & Партнеры | M&A Синдикат",
    "url": "https://structurepartners.ru",
    "description": "Институциональный M&A-бутик для собственников частных медицинских клиник. Сопровождение сделок от 50 до 500 млн рублей.",
    "publisher": {
      "@type": "Organization",
      "name": "S&P Structure & Partners"
    }
  };

  return (
    <section 
      id={id}
      aria-label="Институциональный M&A-бутик в сфере медицины"
      className="relative w-full min-h-[calc(100vh-2rem)] flex flex-col justify-between bg-[#FAF8F5] text-[#141416] overflow-hidden pt-20 md:pt-24 border-b border-[#EAE5DE]"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={10} />

      
      

      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      {/* Тактильный рельеф бумаги слоновой кости (4 вида рельефности) */}
      

      {/* 2026 WebGL / Canvas Liquid Displacement & Wave Distortion */}
      <LiquidLensCanvas className="opacity-30" />

      {/* Мягкий нейтрально-теплый рассеянный свет сверху */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[480px] bg-gradient-to-b from-[#F2EDE4]/70 via-transparent to-transparent pointer-events-none" />

      {/* ЦЕНТРАЛЬНЫЙ КОНТЕНТНЫЙ КОНТУР */}
      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-8 md:gap-12 py-6 md:py-12">
        
        {/* 1. НАДЗАГОЛОВОК + ИНТЕРАКТИВНЫЙ СЕЛЕКТОР 4 ВИДОВ РЕЛЬЕФНОСТИ */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <motion.div 
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2.5 px-3.5 py-1.5 bg-[#FAF7F0]/95 border border-[#8C6D37]/35 w-fit rounded-[2px] shadow-[0_2px_8px_-2px_rgba(140,109,55,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-md select-none"
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8C6D37] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8C6D37]" />
            </span>
            <span 
              className="luxury-eyebrow text-[#8C6D37] font-semibold uppercase tracking-[0.14em] whitespace-nowrap"
              
            >
              ИНСТИТУЦИОНАЛЬНЫЙ M&A В ЧАСТНОЙ МЕДИЦИНЕ // СИНДИКАТ 2026
            </span>
          </motion.div>

          {/* Интерактивный тактильный селектор: 4 вида рельефности бумаги */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="self-start lg:self-auto"
          >
            
          </motion.div>
        </div>

        {/* 2. ГЛАВНЫЙ КИНЕТИЧЕСКИЙ ЗАГОЛОВОК */}
        <div className="flex flex-col gap-2">
          <h1 className="sr-only">Продажа медицинской клиники, оценка стоимости медцентра и M&A-сделки в медицине</h1>
          <motion.h2 
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="viewport-hero font-serif font-semibold tracking-[-0.03em] leading-[1.1] text-[#141416] max-w-5xl select-none"
            
          >
            Институциональная архитектура{" "}
            <span 
              className="text-[#8C6D37] italic font-serif"
              
            >
              M&amp;A-сделок
            </span>{" "}
            в частной медицине
          </motion.h2>
        </div>

        {/* 3. ПОДЗАГОЛОВОК (H3: С ТАКТИЛЬНОЙ ГЛУБИНОЙ ПОГРУЖЕНИЯ В БУМАГУ) */}
        <motion.h3 
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="text-[17px] sm:text-[19px] leading-[1.6] text-[#2D3748] font-sans font-normal max-w-[70ch]"
          
        >
          Мы не продаем клиники через открытые доски объявлений. Мы упаковываем активы по стандартам институциональных фондов, устраняем налоговые риски до выхода на рынок и защищаем капитал от дисконтов на аудите.
        </motion.h3>

        {/* 4. КВАЛИФИКАЦИОННЫЙ БЕЙДЖ (STANDALONE FILTER) С ТИСНЕНИЕМ */}
        <motion.div 
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex flex-wrap items-center gap-3 px-4 py-2 bg-[#FAF7F0]/90 border border-[#DDD5C7] font-mono text-[11px] sm:text-[12px] text-[#141416] w-fit rounded-[2px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_1px_3px_rgba(45,35,20,0.05)]"
        >
          <span 
            className="text-[#8C6D37] font-bold tracking-[0.15em]"
            
          >
            [ ИНСТИТУЦИОНАЛЬНЫЙ СТАНДАРТ M&amp;A ]
          </span>
          <span 
            className="tracking-wider uppercase text-[#141416]/85 font-semibold"
            
          >
            Стоматологии и медцентры · Выручка 50–500 млн ₽
          </span>
        </motion.div>

        {/* 5. SINGLE PRIMARY CTA SYSTEM (TACTILE BRUTALISM) */}
        <motion.div 
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-3 pt-2"
        >
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            {/* Primary Action */}
            <Link
              href="#vulnerability-scoring"
              className="group inline-flex items-center justify-between sm:justify-center gap-6 px-8 py-4 bg-[#141416] hover:bg-[#2C3238] text-white text-[14px] font-semibold tracking-wide rounded-[2px] transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_4px_16px_rgba(20,20,22,0.25)] border border-[#2D3136]"
            >
              <span >
                Инициировать M&amp;A-Scoring (Strict NDA)
              </span>
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform text-[#8C6D37]" />
            </Link>

            {/* Secondary Action - Subordinated with Deboss */}
            <Link
              href="#valuation-matrix"
              className="inline-flex items-center justify-between sm:justify-center gap-3 px-8 py-4 text-[14px] font-medium text-[#475569] hover:text-[#141416] bg-[#FAF7F0]/60 hover:bg-[#FAF7F0] border border-[#DDD5C7] rounded-[2px] transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(45,35,20,0.04)]"
            >
              <span >
                Изучить анатомию оценки
              </span>
              <ArrowDown size={15} className="text-[#8C6D37]" />
            </Link>
          </div>

          {/* Calibrated Microcopy with Deboss */}
          <div 
            className="flex items-center gap-2 text-[11px] font-mono text-[#475569] tracking-wide pt-1"
            
          >
            <Shield size={12} className="text-[#8C6D37] flex-shrink-0" />
            <span>3 минуты. 12 контрольных маркеров. Zero-Trace Protocol.</span>
          </div>
        </motion.div>

        {/* =========================================================================
            6. BENTO GRID 2.0 (Модульная сетка с тактильным тиснением карточек и текста)
            ========================================================================= */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-4"
        >
          {/* Bento Block 1: Focus Node (lg:col-span-5) */}
          <LiquidGlassCard isFeatured className="lg:col-span-5 p-7 sm:p-8 flex flex-col justify-between gap-6 bg-[#FAF8F5]/85 border-[#DDD5C7] shadow-[inset_0_1px_2px_rgba(70,55,30,0.06),0_1px_0_rgba(255,255,255,0.95),0_8px_24px_-6px_rgba(45,35,20,0.06)]">
            <div className="flex flex-col gap-4">
              <div className="flex items-baseline justify-between border-b border-[#E3DDD2] pb-4">
                <span 
                  className="luxury-eyebrow text-[#8C6D37] flex items-center gap-1.5 font-bold tracking-widest"
                  
                >
                  <Activity size={13} className="text-[#8C6D37]" />
                  МОДУЛЬ 01 // ЦЕЛЕВОЙ КОРИДОР
                </span>
                <span className="font-mono text-[10px] text-[#8C6D37] bg-[#8C6D37]/10 px-2 py-0.5 rounded-[2px] font-bold uppercase border border-[#8C6D37]/20">
                  Institutional
                </span>
              </div>
              <div 
                className="font-serif text-4xl text-[#141416] tracking-tight tnum font-semibold select-none"
                
              >
                50 – 500 млн ₽
              </div>
              <p 
                className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal"
                
              >
                Фокус строго на устойчивых медицинских центрах. Защищаем основателя от продажи клиники по цене старого оборудования.
              </p>
            </div>

            <div 
              className="pt-4 border-t border-[#E3DDD2] flex items-center justify-between font-mono text-[11px] text-[#475569]"
              
            >
              <span>Мультипликатор EBITDA:</span>
              <span 
                className="text-[#8C6D37] font-bold"
                
              >
                До 7.5x на закрытии
              </span>
            </div>
          </LiquidGlassCard>

          {/* Bento Block 2: Forensic Depth (lg:col-span-4) */}
          <LiquidGlassCard className="lg:col-span-4 p-7 sm:p-8 flex flex-col justify-between gap-6 bg-[#FAF8F5]/85 border-[#DDD5C7] shadow-[inset_0_1px_2px_rgba(70,55,30,0.06),0_1px_0_rgba(255,255,255,0.95),0_8px_24px_-6px_rgba(45,35,20,0.06)]">
            <div className="flex flex-col gap-4">
              <div className="flex items-baseline justify-between border-b border-[#E3DDD2] pb-4">
                <span 
                  className="luxury-eyebrow text-[#5C5853] font-bold tracking-widest"
                  
                >
                  МОДУЛЬ 02 // ФОРЕНЗИК
                </span>
                <span className="font-mono text-[10px] text-[#5C5853] bg-[#FAF7F0] px-2 py-0.5 rounded-[2px] font-bold border border-[#DDD5C7]">
                  604 точки
                </span>
              </div>
              <div 
                className="font-serif text-4xl text-[#141416] tracking-tight tnum font-semibold select-none"
                
              >
                604 маркера
              </div>
              <p 
                className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal"
                
              >
                Сплошной аудит: от сходимости ЕНС и ЕГИСЗ до договоров аренды с пунктами о смене контроля (Change of Control).
              </p>
            </div>

            <div 
              className="flex items-center gap-2 text-[#059669] font-mono text-[11px] pt-4 border-t border-[#E3DDD2] font-bold"
              
            >
              <CheckCircle2 size={13} className="text-[#059669]" />
              <span>Риск отказа инвестора снижен до 0%</span>
            </div>
          </LiquidGlassCard>

          {/* Bento Block 3: Air-Gap Discretion (lg:col-span-3) */}
          <LiquidGlassCard className="lg:col-span-3 p-7 sm:p-8 flex flex-col justify-between gap-6 bg-[#FAF8F5]/85 border-[#DDD5C7] shadow-[inset_0_1px_2px_rgba(70,55,30,0.06),0_1px_0_rgba(255,255,255,0.95),0_8px_24px_-6px_rgba(45,35,20,0.06)]">
            <div className="flex flex-col gap-4">
              <div className="flex items-baseline justify-between border-b border-[#E3DDD2] pb-4">
                <span 
                  className="luxury-eyebrow text-[#5C5853] font-bold tracking-widest"
                  
                >
                  МОДУЛЬ 03 // СЕКРЕТНОСТЬ
                </span>
                <Lock size={13} className="text-[#8C6D37]" />
              </div>
              <div 
                className="font-serif text-4xl text-[#141416] tracking-tight tnum font-semibold select-none"
                
              >
                0% утечки
              </div>
              <p 
                className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal"
                
              >
                Персонал и конкуренты узнают о смене собственника последними. Защита коммерческой тайны по 98-ФЗ.
              </p>
            </div>

            <div 
              className="font-mono text-[11px] text-[#475569] pt-4 border-t border-[#E3DDD2] font-medium uppercase tracking-wider"
              
            >
              Air-Gapped NDA Protocol
            </div>
          </LiquidGlassCard>
        </motion.div>

      </div>

      {/* =========================================================================
          7. VIEWPORT MARQUEE STREAM (Кинетическая бегущая строка 2026)
          ========================================================================= */}
      <div className="relative z-10 w-full border-y border-[#EAE5DE] bg-[#FAF8F5]/90 backdrop-blur-md py-3 overflow-hidden">
        <div className="kinetic-marquee-container">
          <div 
            className="kinetic-marquee-track font-mono text-[11px] text-[#475569] uppercase tracking-[0.18em]"
            
          >
            <span className="px-6 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#8C6D37] inline-block" />
              SWISS LIGHT LUXURY M&amp;A ADVISORY
            </span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 text-[#141416] font-bold">MULTIPLE UP TO 7.5x EBITDA</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 font-medium">604 FORENSIC AUDIT POINTS</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 text-[#059669] font-bold">AIR-GAP DISCRETION PROTOCOL</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 font-medium">TAX REFORM 2025–2026 COMPLIANCE</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 text-[#8C6D37] font-bold">NO COLD LISTINGS</span>
            <span className="px-6 text-[#334155]">•</span>
          </div>

          <div 
            aria-hidden="true" 
            className="kinetic-marquee-track font-mono text-[11px] text-[#475569] uppercase tracking-[0.18em]"
            
          >
            <span className="px-6 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#8C6D37] inline-block" />
              SWISS LIGHT LUXURY M&amp;A ADVISORY
            </span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 text-[#141416] font-bold">MULTIPLE UP TO 7.5x EBITDA</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 font-medium">604 FORENSIC AUDIT POINTS</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 text-[#059669] font-bold">AIR-GAP DISCRETION PROTOCOL</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 font-medium">TAX REFORM 2025–2026 COMPLIANCE</span>
            <span className="px-6 text-[#334155]">•</span>
            <span className="px-6 text-[#8C6D37] font-bold">NO COLD LISTINGS</span>
            <span className="px-6 text-[#334155]">•</span>
          </div>
        </div>
      </div>

    </section>
  );
}

